
import type {
  RemitoBaseData,
  Ubicacion,
  RemitoSyncPayloadOptionalCheckOut,
  CheckInPayload,
  DeliveryNote,
  PayloadLocation,
  DeviceInfo,
  FingerprintId
} from '@core/remito.model';


export interface CheckInPayloadSource {
  id: string;
  createdAt: number | string;
  ubicacion: Ubicacion | null | undefined;
  fingerprintId?: FingerprintId;
  deviceInfo?: DeviceInfo;
}

export interface CheckOutPayloadSource {
  id?: string;
  createdAt: number | string;
  ubicacion: Ubicacion | null | undefined;
  deviceInfo?: DeviceInfo;
  fingerprintId?: FingerprintId;
}


interface BuildSyncPayloadOptions {
  sessionId: string;
  checkIn: CheckInPayloadSource;
  checkOut?: CheckOutPayloadSource;
  deliveryNotes: {
    scanData: RemitoBaseData;
    observation: string;
    imageKey: string;
    image: Blob;
    pageNumber?: number;
  }[];
}


// Nuevo builder para el payload de sincronización de remitos
export function buildRemitoSyncPayload(options: BuildSyncPayloadOptions & { checkOut?: CheckOutPayloadSource }): RemitoSyncPayloadOptionalCheckOut {
  const { sessionId, checkIn, checkOut, deliveryNotes } = options;

  if (!sessionId?.trim()) {
    throw new Error('sessionId es requerido');
  }

  // CheckIn
  const checkInLocation = toPayloadLocation(checkIn?.ubicacion, 'la ubicación del Check-in');
  const fingerprintId = checkIn.fingerprintId || 'unknown';
  const baseDeviceInfo = checkIn.deviceInfo || buildDefaultDeviceInfo(fingerprintId);

  const checkInPayload: CheckInPayload = {
    id: checkIn.id,
    createdAt: toISOString(checkIn.createdAt),
    location: checkInLocation,
    deviceInfo: {
      ...baseDeviceInfo,
      fingerprintId
    }
  };

  // CheckOut - solo si se proporciona explícitamente
  const checkOutPayload = checkOut ? {
    id: checkOut.id ?? `checkout-${Date.now().toString(36)}`,
    createdAt: toISOString(checkOut.createdAt),
    location: toPayloadLocation(checkOut.ubicacion, 'la ubicación del Check-out'),
    deviceInfo: {
      ...(checkOut.deviceInfo || baseDeviceInfo),
      fingerprintId: checkOut.fingerprintId || baseDeviceInfo.fingerprintId || fingerprintId
    }
  } : undefined;

  // DeliveryNotes
  const notes: DeliveryNote[] = deliveryNotes.map(note => {
    if (!note.scanData?.qrCode?.trim()) {
      throw new Error('Falta qrCode en deliveryNote');
    }
    if (!note.imageKey?.trim()) {
      throw new Error('Falta imageKey en deliveryNote');
    }
    // Permitir image undefined/null al guardar localmente (IndexedDB)
    // Solo exigir Blob en el flujo de sincronización/API
    return {
      qrCode: note.scanData.qrCode,
      scanDate: toISOString(note.scanData.fechaEscaneo),
      location: toPayloadLocation(note.scanData.ubicacion, 'ubicación de deliveryNote'),
      observation: note.observation?.trim() ?? '',
      imageKey: note.imageKey,
      image: note.image ?? undefined,
      pageNumber: note.pageNumber,
    };
  });

  return {
    sessionId,
    checkIn: checkInPayload,
    checkOut: checkOutPayload,
    deliveryNotes: notes,
  };
}

export function toPayloadLocation(location?: Ubicacion | null, context?: string): PayloadLocation {
  if (!location) {
    throw new Error(`No se encontró ${context ?? 'la ubicación'} para construir el remito`);
  }
  return {
    latitude: location.latitud,
    longitude: location.longitud,
    precision: location.precision,
  };
}


// Convierte a string ISO (si es Date o timestamp)
export function toISOString(value: Date | number | string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string') {
    // Si ya es ISO, devolver tal cual
    if (!Number.isNaN(Date.parse(value))) {
      return new Date(value).toISOString();
    }
  }
  throw new Error('No se pudo convertir la fecha a string ISO');
}

function buildDefaultDeviceInfo(fingerprintId: FingerprintId): DeviceInfo {
  return {
    deviceType: 'mobile',
    os: 'Unknown',
    version: 'unknown',
    mobile: true,
    resolution: '0x0',
    networkType: 'offline',
    timeHour: 0,
    timezone: 'unknown',
    fingerprintId
  };
}
